import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * A custom element that toggles between "Subscribe & Save" and "One-time purchase"
 * purchase modes for products with selling plans (subscriptions).
 *
 * Keeps the hidden `selling_plan` input on the add-to-cart form in sync with the
 * active mode and the currently selected variant, and toggles which pre-rendered
 * price block (subscription vs one-time) is visible in the variant picker.
 */
export default class SubscriptionToggle extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleCheckboxChange);

    const variantPicker = this.closest('variant-picker');
    variantPicker?.addEventListener('change', () => this.#syncSellingPlan());

    // `variant-picker` re-renders its subtree (including this component's buttons)
    // from freshly fetched, server-rendered HTML whenever the selected variant
    // changes. That fetch resolves asynchronously and always renders the toggle in
    // its default (subscribed) visual state, so re-sync once it lands to reapply
    // whichever mode is actually in effect (tracked on the variant-picker itself,
    // which morph leaves untouched since it only patches children).
    variantPicker?.addEventListener(StandardEvents.productSelect, async (event) => {
      await event.promise.catch(() => {});
      this.#syncButtons();
      this.#syncSellingPlan();
    });

    this.#syncButtons();
    this.#syncSellingPlan();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleCheckboxChange);
  }

  /**
   * Handles the "Sub 2" checkbox-card style: checked = subscription, unchecked = one-time.
   * Stops propagation so the variant-picker doesn't mistake it for an option change.
   * @param {Event} event
   */
  #handleCheckboxChange = (event) => {
    const checkbox = event.target;
    if (!(checkbox instanceof HTMLInputElement) || !checkbox.hasAttribute('data-subscription-checkbox')) return;
    event.stopPropagation();

    const mode = checkbox.checked ? 'subscription' : 'one_time';
    this.closest('variant-picker')?.setAttribute('data-purchase-mode', mode);

    this.#syncButtons();
    this.#syncSellingPlan();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-mode]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const mode = button.dataset.mode;
    if (!mode || mode === this.#currentMode) return;

    const variantPicker = this.closest('variant-picker');
    variantPicker?.setAttribute('data-purchase-mode', mode);

    this.#syncButtons();
    this.#syncSellingPlan();
  };

  get #currentMode() {
    return this.closest('variant-picker')?.getAttribute('data-purchase-mode') ?? 'subscription';
  }

  #syncButtons() {
    const currentMode = this.#currentMode;
    for (const button of this.querySelectorAll('[data-mode]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.setAttribute('aria-pressed', String(button.dataset.mode === currentMode));
    }
    for (const checkbox of this.querySelectorAll('[data-subscription-checkbox]')) {
      if (!(checkbox instanceof HTMLInputElement)) continue;
      checkbox.checked = currentMode === 'subscription';
    }
  }

  #syncSellingPlan() {
    const variantPicker = this.closest('variant-picker');
    const form = document.querySelector('form[data-type="add-to-cart-form"]');
    const sellingPlanInput = form?.querySelector('[name="selling_plan"]');

    if (!(sellingPlanInput instanceof HTMLInputElement)) return;

    if (this.#currentMode === 'one_time') {
      sellingPlanInput.disabled = true;
      return;
    }

    const checkedRadio = variantPicker?.querySelector('input:checked[data-selling-plan-id]');
    const sellingPlanId = checkedRadio instanceof HTMLInputElement ? checkedRadio.dataset.sellingPlanId : undefined;

    sellingPlanInput.disabled = !sellingPlanId;
    if (sellingPlanId) {
      sellingPlanInput.value = sellingPlanId;
    }
  }
}

if (!customElements.get('subscription-toggle')) {
  customElements.define('subscription-toggle', SubscriptionToggle);
}
